import { Component } from '@theme/component';

/* ------------------------------------------------------------------ *
 * STRYDE anatomy — hotspots on the product shot, linked to a list.
 * ------------------------------------------------------------------ */
class StrydeAnatomyComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#apply(0);
  }

  /** @param {Event} event */
  select(event) {
    const hit = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-index]') : null
    );
    if (!hit) return;
    this.#apply(Number(hit.dataset.index));
  }

  /** @param {number} index */
  #apply(index) {
    for (const dot of this.querySelectorAll('.stryde-anatomy__dot')) {
      dot.setAttribute('aria-current', String(Number(dot.getAttribute('data-index')) === index));
    }
    for (const row of this.querySelectorAll('.stryde-anatomy__row')) {
      row.setAttribute('aria-expanded', String(Number(row.getAttribute('data-index')) === index));
    }
  }
}

if (!customElements.get('stryde-anatomy-component')) {
  customElements.define('stryde-anatomy-component', StrydeAnatomyComponent);
}

/* ------------------------------------------------------------------ *
 * STRYDE size finder — thigh + weight in, a size out.
 * Publishes `stryde:size` so the bundle block can follow along.
 * ------------------------------------------------------------------ */
class StrydeSizeFinderComponent extends Component {
  /** Values as typed, kept in cm and kg so switching units never changes the result. */
  /** @type {{ thigh: number, weight: number }} */ #metric = { thigh: NaN, weight: NaN };
  /** @type {{ thigh: string, weight: string }} */ #unit = { thigh: 'in', weight: 'lb' };

  connectedCallback() {
    super.connectedCallback();
    this.#unit = { thigh: this.dataset.thighUnit || 'in', weight: this.dataset.weightUnit || 'lb' };
  }

  /** @param {'thigh' | 'weight'} field @param {number} value @param {string} unit */
  static toMetric(field, value, unit) {
    if (field === 'thigh') return unit === 'in' ? value * 2.54 : value;
    return unit === 'lb' ? value / 2.20462 : value;
  }

  /** @param {Event} event */
  unit(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-u]') : null;
    if (!(button instanceof HTMLElement)) return;
    const field = /** @type {'thigh' | 'weight'} */ (button.dataset.field);
    const next = button.dataset.u ?? '';
    if (!field || this.#unit[field] === next) return;
    this.#unit[field] = next;
    button.parentElement?.querySelectorAll('[data-u]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    const input = /** @type {HTMLInputElement | undefined} */ (this.refs[field]);
    if (!input) return;
    input.placeholder = button.textContent?.trim() || next;
    const metric = this.#metric[field];
    if (Number.isFinite(metric)) {
      const shown = field === 'thigh' ? (next === 'in' ? metric / 2.54 : metric) : (next === 'lb' ? metric * 2.20462 : metric);
      input.value = String(Math.round(shown * 10) / 10);
    }
  }

  /** @param {Event} [event] */
  read(event) {
    const input = event?.target;
    for (const field of /** @type {const} */ (['thigh', 'weight'])) {
      if (input && input !== this.refs[field]) continue;
      const raw = parseFloat(/** @type {HTMLInputElement | undefined} */ (this.refs[field])?.value ?? '');
      this.#metric[field] = Number.isFinite(raw) ? StrydeSizeFinderComponent.toMetric(field, raw, this.#unit[field]) : NaN;
    }
    this.evaluate();
  }

  get #rows() {
    return /** @type {HTMLTableRowElement[]} */ ([...this.querySelectorAll('tbody tr[data-size]')]);
  }

  evaluate() {
    const { thigh, weight } = this.#metric;

    if (!Number.isFinite(thigh) && !Number.isFinite(weight)) {
      this.#mark(null);
      this.#say(this.dataset.hint, false);
      return;
    }

    const hit = this.#rows.find((row) => {
      const d = row.dataset;
      const okThigh = Number.isFinite(thigh) && thigh >= Number(d.thighMin) && thigh <= Number(d.thighMax);
      const okWeight = Number.isFinite(weight) && weight >= Number(d.weightMin) && weight <= Number(d.weightMax);
      return okThigh || okWeight;
    });

    if (!hit) {
      this.#mark(null);
      this.#say(this.dataset.outOfRange, false);
      return;
    }

    this.#mark(hit);
    this.#say(this.dataset.recommend, true, hit.dataset.size);
    this.#publish(hit.dataset.size);
  }

  /** @param {Event} event */
  pickRow(event) {
    const row = /** @type {HTMLTableRowElement | null} */ (
      event.target instanceof Element ? event.target.closest('tr[data-size]') : null
    );
    if (!row) return;
    this.#mark(row);
    this.#say(this.dataset.selected, true, row.dataset.size);
    this.#publish(row.dataset.size);
  }

  /** @param {HTMLElement | null} row */
  #mark(row) {
    for (const candidate of this.#rows) {
      candidate.setAttribute('aria-current', String(candidate === row));
    }
  }

  /**
   * @param {string | undefined} text
   * @param {boolean} withSize
   * @param {string} [size]
   */
  #say(text, withSize, size) {
    const out = this.refs.out;
    if (!out) return;
    out.textContent = '';
    if (withSize && size) {
      const badge = document.createElement('b');
      badge.textContent = size;
      out.append(badge);
    }
    const span = document.createElement('span');
    if (!withSize) span.className = 'stryde-sf__muted';
    span.textContent = text ?? '';
    out.append(span);
  }

  /** @param {string | undefined} size */
  #publish(size) {
    if (!size) return;
    document.dispatchEvent(new CustomEvent('stryde:size', { detail: { size } }));
  }
}

if (!customElements.get('stryde-size-finder-component')) {
  customElements.define('stryde-size-finder-component', StrydeSizeFinderComponent);
}

/* ------------------------------------------------------------------ *
 * STRYDE bundle — tiers, a size/side/colour picker per brace,
 * a multi-line add to cart and the sticky bar.
 * ------------------------------------------------------------------ */

/** @typedef {{ id: number, a: boolean, o: string[] }} StrydeVariant */
/** @typedef {'size' | 'side' | 'colour'} StrydeGroup */
/** @typedef {Record<StrydeGroup, string | null>} StrydeUnit */

const GROUPS = /** @type {StrydeGroup[]} */ (['size', 'side', 'colour']);

/** An amount in the shared price style (class .price, see the STRYDE topbar section). */
const priceEl = (/** @type {string | undefined} */ text, modifier = '') => {
  const el = document.createElement('span');
  el.className = `price ${modifier}`.trim();
  el.textContent = text ?? '';
  return el;
};

class StrydeBundleComponent extends Component {
  /** @type {StrydeVariant[]} */ #variants = [];
  /** @type {Record<string, number>} */ #media = {};
  /** @type {Record<StrydeGroup, number>} */ #at = { size: -1, side: -1, colour: -1 };
  /** @type {StrydeUnit[]} */ #units = [];
  #tier = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */ #timer;
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const json = (/** @type {string | undefined} */ raw, /** @type {any} */ fallback) => {
      try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
    };
    this.#variants = json(this.dataset.variants, []);
    this.#media = json(this.dataset.media, {});
    this.#at = { ...this.#at, ...json(this.dataset.at, {}) };

    const { signal } = this.#abort;
    document.addEventListener('stryde:size', (event) => {
      const size = /** @type {CustomEvent} */ (event).detail?.size;
      if (size && this.#units[0]) { this.#units[0].size = size; this.#render(); }
    }, { signal });

    const buyBlock = this.closest('.product-information') ?? this;
    const onScroll = () => {
      const rect = buyBlock.getBoundingClientRect();
      const legal = document.querySelector('.stryde-legal');
      const footerInView = Boolean(legal && legal.getBoundingClientRect().top < window.innerHeight);
      const show = rect.bottom < 120 && !footerInView;
      this.refs.sticky?.classList.toggle('show', show);
      this.refs.sticky?.setAttribute('aria-hidden', String(!show));
    };
    // Horizon scrolls .page-wrapper, not the window; capture catches either.
    document.addEventListener('scroll', onScroll, { passive: true, capture: true, signal });

    this.#decorateGallery();
    this.#buildSticky();
    this.#wireHints();
    this.pickTier(Math.max(0, Number(this.dataset.default) || 0));
    onScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
    clearTimeout(this.#timer);
  }

  /** Badge + "1 / 13" counter on Horizon's gallery (the mockup has both; Horizon has neither). */
  #decorateGallery() {
    const host = this.closest('.shopify-section')?.querySelector('media-gallery slideshow-component');
    if (!host || host.querySelector('.sb-gcount')) return;
    if (this.dataset.galleryBadge) {
      const badge = document.createElement('span');
      badge.className = 'sb-gbadge';
      badge.textContent = this.dataset.galleryBadge;
      host.append(badge);
    }
    const slides = [...host.querySelectorAll('slideshow-slide')];
    if (slides.length < 2) return;
    const count = document.createElement('span');
    count.className = 'sb-gcount';
    host.append(count);
    const update = () => {
      const i = Math.max(0, slides.findIndex((s) => s.getAttribute('aria-hidden') === 'false'));
      count.textContent = `${i + 1} / ${slides.length}`;
    };
    update();
    const watch = new MutationObserver(update);
    slides.forEach((s) => watch.observe(s, { attributes: true, attributeFilter: ['aria-hidden'] }));
    this.#abort.signal.addEventListener('abort', () => watch.disconnect());
  }

  get #cards() { return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.sb-tier')]); }
  get #card() { return this.#cards[this.#tier] ?? this.#cards[0]; }

  /** @param {StrydeGroup} group */
  #values(group) {
    const at = this.#at[group];
    /** @type {string[]} */ const out = [];
    if (at < 0) return out;
    for (const v of this.#variants) if (v.o[at] && !out.includes(v.o[at])) out.push(v.o[at]);
    return out;
  }

  /** @param {StrydeUnit} unit */
  #variant(unit) {
    return this.#variants.find((v) => GROUPS.every((g) => this.#at[g] < 0 || v.o[this.#at[g]] === unit[g]));
  }

  /** @param {number | Event} input */
  pickTier(input) {
    const index = typeof input === 'number'
      ? input
      : this.#cards.indexOf(/** @type {HTMLElement} */ (/** @type {Element} */ (input.target).closest('.sb-tier')));
    this.#tier = Math.max(0, Math.min(index, this.#cards.length - 1));
    const qty = Number(this.#card?.dataset.qty || 1);
    const first = this.#units[0];
    this.#units = Array.from({ length: qty }, (_, i) => ({
      size: this.#units[i]?.size ?? first?.size ?? this.#values('size')[0] ?? null,
      side: this.#units[i]?.side ?? null,
      colour: this.#units[i]?.colour ?? first?.colour ?? this.#values('colour')[0] ?? null,
    }));
    this.#cards.forEach((card, i) => {
      card.classList.toggle('on', i === this.#tier);
      card.querySelector('.sb-th')?.setAttribute('aria-pressed', String(i === this.#tier));
    });
    this.#render();
  }

  /** @param {Event} event */
  pick(event) {
    const button = event.target instanceof Element ? event.target.closest('[data-value]') : null;
    const group = /** @type {StrydeGroup | undefined} */ (button?.closest('[data-group]')?.getAttribute('data-group') ?? undefined);
    const index = Number(button?.closest('[data-unit]')?.getAttribute('data-unit') ?? 0);
    if (!(button instanceof HTMLElement) || !group || !this.#units[index]) return;
    this.#set(index, group, button.dataset.value ?? null);
  }

  /** @param {number} index @param {StrydeGroup} group @param {string | null} value */
  #set(index, group, value) {
    const unit = this.#units[index];
    if (!unit) return;
    unit[group] = value;
    if (group === 'colour' && value) {
      const slide = this.#media[value];
      if (slide >= 0) /** @type {any} */ (document.querySelector('media-gallery'))?.slideshow?.select?.(slide, undefined, { animate: false });
    }
    this.#render();
  }

  /** Size and colour of brace 1 on every other brace; the side stays, since it usually differs. */
  copyFirst() {
    const first = this.#units[0];
    if (!first) return;
    this.#units.forEach((unit, i) => { if (i > 0) { unit.size = first.size; unit.colour = first.colour; } });
    this.#render();
  }

  /** @param {number} index */
  #sideError(index) {
    return (this.dataset.tSideErr ?? '').replace('[n]', String(index + 1));
  }

  toSize() {
    document.getElementById(this.dataset.sizeAnchor || 'size')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  get #ready() { return this.#units.every((u) => u.side); }

  /** The current pick as one line plus the tier total, for the clip player's product card. */
  get selection() {
    const d = this.dataset;
    const qty = this.#units.length;
    const unit = this.#units[0];
    const line = [qty > 1 ? `${qty} ${d.tUnits}` : '', unit?.size, unit?.side ?? d.tMissing, unit?.colour];
    return { line: line.filter(Boolean).join(' · '), price: this.#card?.dataset.total ?? '' };
  }

  #render() {
    const card = this.#card;
    if (!card) return;
    const d = this.dataset;

    card.querySelectorAll('[data-unit]').forEach((el) => {
      const unit = this.#units[Number(el.getAttribute('data-unit'))];
      if (!unit) return;
      el.querySelectorAll('[data-group]').forEach((row) => {
        const group = /** @type {StrydeGroup} */ (row.getAttribute('data-group'));
        row.querySelectorAll('[data-value]').forEach((b) => {
          const value = b.getAttribute('data-value');
          b.setAttribute('aria-checked', String(unit[group] === value));
          const v = this.#variant({ ...unit, side: unit.side ?? this.#values('side')[0] ?? null, [group]: value });
          /** @type {HTMLButtonElement} */ (b).disabled = Boolean(v && !v.a);
        });
      });
      const summary = el.querySelector('[data-summary]');
      if (summary) {
        summary.textContent = '';
        summary.append(unit.size ?? '', ' · ');
        if (unit.side) summary.append(unit.side);
        else { const i = document.createElement('i'); i.textContent = d.tMissing ?? ''; summary.append(i); }
        summary.append(' · ', unit.colour ?? '');
      }
    });

    // Side message under the lines: follows the first brace still without a side, gone once all have one.
    const err = card.querySelector('[data-err]');
    if (err?.textContent) {
      const missing = this.#units.findIndex((u) => !u.side);
      err.textContent = missing < 0 ? '' : this.#sideError(missing);
      if (missing < 0 && this.refs.atc?.getAttribute('aria-describedby') === err.id) this.refs.atc.removeAttribute('aria-describedby');
    }

    const warn = card.querySelector('[data-warn]');
    if (warn instanceof HTMLElement) {
      const sides = this.#units.map((u) => u.side).filter(Boolean);
      warn.hidden = new Set(sides).size === sides.length;
    }

    // Price block(s) on the page; a block set to the single-brace price keeps it.
    for (const holder of document.querySelectorAll('[data-stryde-price]:not([data-static])')) {
      const now = holder.querySelector('[data-stryde-price-now]');
      const was = holder.querySelector('[data-stryde-price-was]');
      const save = holder.querySelector('[data-stryde-price-save]');
      if (now) now.textContent = card.dataset.total ?? '';
      if (was instanceof HTMLElement) { was.textContent = card.dataset.was ?? ''; was.hidden = !card.dataset.was; }
      if (save instanceof HTMLElement) { save.textContent = card.dataset.save ?? ''; save.hidden = !card.dataset.save; }
    }

    this.#renderButtons();
    this.#renderSticky();
  }

  #renderButtons() {
    const d = this.dataset;
    const atc = this.refs.atc;
    if (!atc || atc.classList.contains('added')) return;
    // The button always looks ready; a missing side is flagged on click (see #flagMissing).
    const soldOut = this.#ready && this.#units.some((u) => !this.#variant(u)?.a);
    atc.classList.toggle('soldout', soldOut);
    atc.setAttribute('aria-disabled', String(soldOut));
    if (this.refs.atcLabel) {
      if (soldOut) this.refs.atcLabel.textContent = d.tSold ?? '';
      else this.refs.atcLabel.replaceChildren(`${d.tAdd ?? ''} `, priceEl(this.#card?.dataset.total, 'price--on-dark'));
    }
    if (this.refs.stickyBtn) this.refs.stickyBtn.textContent = soldOut ? d.tSold ?? '' : d.tStickyAdd ?? '';
  }

  #buildSticky() {
    const host = this.refs.stickyOpts;
    if (!host) return;
    for (const group of GROUPS) {
      const values = this.#values(group);
      if (!values.length) continue;
      const wrap = document.createElement('div');
      wrap.className = 'sb-sg';
      wrap.dataset.group = group;
      const label = this.querySelector(`[data-group="${group}"]`)?.closest('.sb-row')?.querySelector('.sb-lab');
      if (label) wrap.append(label.cloneNode(true));
      for (const value of values) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sb-o';
        b.dataset.value = value;
        b.setAttribute('aria-label', value);
        const source = this.querySelector(`[data-group="${group}"] [data-value="${CSS.escape(value)}"] .sb-sw`);
        if (source) b.append(source.cloneNode(true));
        else b.textContent = group === 'side' ? value.charAt(0) : value;
        b.addEventListener('click', () => this.#set(0, group, value));
        wrap.append(b);
      }
      host.append(wrap);
    }
  }

  #renderSticky() {
    const unit = this.#units[0];
    if (!unit) return;
    this.refs.stickyOpts?.querySelectorAll('.sb-sg').forEach((wrap) => {
      const group = /** @type {StrydeGroup} */ (/** @type {HTMLElement} */ (wrap).dataset.group);
      wrap.querySelectorAll('[data-value]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-value') === unit[group])));
    });
    const qty = this.#units.length;
    const d = this.dataset;
    if (this.refs.stickySub) {
      this.refs.stickySub.textContent = [`${qty} ${qty > 1 ? d.tUnits : d.tUnit}`, unit.size, unit.side, unit.colour].filter(Boolean).join(' · ');
    }
    if (this.refs.stickyPrice) this.refs.stickyPrice.textContent = this.#card?.dataset.total ?? '';
    if (this.refs.stickyEach) {
      if (qty > 1) this.refs.stickyEach.replaceChildren(`${qty} ${d.tUnits} · `, priceEl(this.#card?.dataset.each), ` ${d.tEach ?? ''}`);
      else this.refs.stickyEach.textContent = `1 ${d.tUnit}`;
    }
  }

  /** "or [price]/brace in a 4-pack" next to the price: filled from the matching tier, hidden when there is none. */
  #wireHints() {
    for (const hint of document.querySelectorAll('[data-stryde-hint]')) {
      if (!(hint instanceof HTMLElement)) continue;
      const index = this.#cards.findIndex((card) => card.dataset.qty === hint.dataset.qty);
      const card = this.#cards[index];
      const price = hint.querySelector('[data-each]');
      if (!card || !price) { hint.hidden = true; continue; }
      price.textContent = card.dataset.each ?? '';
      hint.hidden = false;
      hint.addEventListener('click', () => {
        this.pickTier(index);
        const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        card.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
      }, { signal: this.#abort.signal });
    }
  }

  stickyAdd() {
    this.addToCart();
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */ #needTimer;

  /** No side yet: scroll to the first brace without one, outline its side row for 2 s and say why. */
  #flagMissing() {
    const index = this.#units.findIndex((u) => !u.side);
    const unitEl = this.#card?.querySelector(`[data-unit="${index}"]`);
    if (!unitEl) return;
    const row = unitEl.querySelector('[data-group="side"]');
    const err = this.#card?.querySelector('[data-err]');
    if (err) {
      err.textContent = this.#sideError(index);
      this.refs.atc?.setAttribute('aria-describedby', err.id);
    }
    if (row) {
      row.classList.remove('sb-need');
      void (/** @type {HTMLElement} */ (row).offsetWidth);
      row.classList.add('sb-need');
      clearTimeout(this.#needTimer);
      this.#needTimer = setTimeout(() => row.classList.remove('sb-need'), 2000);
    }
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    unitEl.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'center' });
  }

  async addToCart() {
    if (!this.#ready) { this.#flagMissing(); return; }
    const lines = this.#units.map((unit) => ({ unit, variant: this.#variant(unit) }));
    if (lines.some(({ variant }) => !variant?.a)) return;

    // One line per variant: two identical braces become quantity 2. The bundle discounts count quantity.
    /** @type {Map<number, number>} */ const perVariant = new Map();
    for (const { variant } of lines) if (variant) perVariant.set(variant.id, (perVariant.get(variant.id) ?? 0) + 1);

    try {
      await /** @type {any} */ (window).Shopify?.actions?.updateCart({
        lines: [...perVariant].map(([id, quantity]) => ({ merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity })),
      }, { event: { context: 'product' } });
    } catch (error) {
      console.error('[stryde] add to cart failed', error);
      return;
    }

    const atc = this.refs.atc;
    if (!atc) return;
    atc.classList.add('added');
    if (this.refs.atcLabel) this.refs.atcLabel.textContent = this.dataset.tAdded ?? '';
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => { atc.classList.remove('added'); this.#renderButtons(); }, 1700);
  }
}

if (!customElements.get('stryde-bundle-component')) {
  customElements.define('stryde-bundle-component', StrydeBundleComponent);
}

/* ------------------------------------------------------------------ *
 * STRYDE colour swap — swatches that swap the first image in a section.
 * ------------------------------------------------------------------ */
class StrydeColourSwapComponent extends Component {
  /** @param {Event} event */
  pick(event) {
    const button = event.target instanceof Element ? event.target.closest('.cs-b') : null;
    if (!(button instanceof HTMLElement)) return;
    this.querySelectorAll('.cs-b').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    const img = this.closest('.shopify-section')?.querySelector('.image-block img, img');
    if (!(img instanceof HTMLImageElement) || !button.dataset.src) return;
    img.srcset = button.dataset.srcset ?? '';
    img.src = button.dataset.src;
    if (button.dataset.alt) img.alt = button.dataset.alt;
  }
}

if (!customElements.get('stryde-colour-swap-component')) {
  customElements.define('stryde-colour-swap-component', StrydeColourSwapComponent);
}

/* ------------------------------------------------------------------ *
 * STRYDE clips — looping thumbnails in the buy box and a full-screen
 * story player with an add-to-cart card.
 * ------------------------------------------------------------------ */
class StrydeClipsComponent extends Component {
  #index = 0;
  /** @type {HTMLElement | null} */ #opener = null;
  /** @type {IntersectionObserver | undefined} */ #io;
  #start = { x: 0, y: 0 };
  #abort = new AbortController();

  /** Theme blocks can't cap their children, so only the first four count. */
  get #thumbs() { return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.scl-t')].slice(0, 4)); }

  get #bundle() {
    return /** @type {any} */ (this.closest('.product-information')?.querySelector('stryde-bundle-component')
      ?? document.querySelector('stryde-bundle-component'));
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abort;
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#io = new IntersectionObserver((entries) => {
        for (const { target, isIntersecting } of entries) {
          const video = /** @type {HTMLVideoElement} */ (target);
          if (isIntersecting) video.play().catch(() => {});
          else video.pause();
        }
      }, { threshold: 0.4 });
      for (const thumb of this.#thumbs) {
        const video = thumb.querySelector('video');
        if (video) { video.muted = true; this.#io.observe(video); }
      }
    }

    const { video, stage, dialog } = this.refs;
    video?.addEventListener('timeupdate', () => this.#progress(), { signal });
    video?.addEventListener('ended', () => this.#go(this.#index + 1), { signal });
    dialog?.addEventListener('close', () => this.#closed(), { signal });
    stage?.addEventListener('pointerdown', (event) => { this.#start = { x: event.clientX, y: event.clientY }; }, { signal });
    stage?.addEventListener('pointerup', (event) => this.#gesture(event), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
    this.#abort.abort();
  }

  /** @param {Event} event */
  open(event) {
    const thumb = event.target instanceof Element ? event.target.closest('.scl-t') : null;
    const index = this.#thumbs.indexOf(/** @type {HTMLElement} */ (thumb));
    const { dialog, bars, pick } = this.refs;
    if (index < 0 || !(dialog instanceof HTMLDialogElement)) return;
    this.#opener = /** @type {HTMLElement} */ (thumb);
    if (bars) bars.replaceChildren(...this.#thumbs.map(() => document.createElement('i')));
    const current = this.#bundle?.selection;
    if (pick) {
      pick.textContent = current?.line ?? '';
      if (current?.price) pick.append(current.line ? ' · ' : '', priceEl(current.price));
    }
    dialog.showModal();
    this.#go(index);
  }

  /** @param {number} index */
  #go(index) {
    const thumb = this.#thumbs[index];
    const video = /** @type {HTMLVideoElement | undefined} */ (this.refs.video);
    if (!thumb || !video) { this.close(); return; }
    this.#index = index;
    const hls = thumb.dataset.hls;
    video.poster = thumb.dataset.poster ?? '';
    // The full clip only loads here, never with the page.
    video.src = hls && video.canPlayType('application/vnd.apple.mpegurl') ? hls : thumb.dataset.src ?? '';
    video.play().catch(() => {});
    if (this.refs.caption) this.refs.caption.textContent = thumb.dataset.caption ?? '';
    if (this.refs.count) this.refs.count.textContent = `${index + 1} / ${this.#thumbs.length}`;
    this.refs.bars?.querySelectorAll('i').forEach((bar, i) => {
      /** @type {HTMLElement} */ (bar).style.setProperty('--p', i < index ? '1' : '0');
    });
  }

  #progress() {
    const video = /** @type {HTMLVideoElement | undefined} */ (this.refs.video);
    const bar = /** @type {HTMLElement | undefined} */ (this.refs.bars?.children[this.#index]);
    if (video && bar && video.duration) bar.style.setProperty('--p', String(video.currentTime / video.duration));
  }

  /** Tap left or right half = previous or next clip; swipe down = close. @param {PointerEvent} event */
  #gesture(event) {
    if (event.target instanceof Element && event.target.closest('button, .scl-card')) return;
    const dx = event.clientX - this.#start.x;
    const dy = event.clientY - this.#start.y;
    if (dy > 80 && Math.abs(dy) > Math.abs(dx)) { this.close(); return; }
    if (Math.abs(dx) > 12 || Math.abs(dy) > 12) return;
    const rect = /** @type {HTMLElement} */ (event.currentTarget).getBoundingClientRect();
    this.#go(event.clientX - rect.left < rect.width / 2 ? Math.max(0, this.#index - 1) : this.#index + 1);
  }

  mute() {
    const video = /** @type {HTMLVideoElement | undefined} */ (this.refs.video);
    if (!video) return;
    video.muted = !video.muted;
    this.refs.mute?.setAttribute('aria-pressed', String(!video.muted));
  }

  close() {
    const dialog = this.refs.dialog;
    if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
  }

  #closed() {
    const video = /** @type {HTMLVideoElement | undefined} */ (this.refs.video);
    if (video) { video.pause(); video.removeAttribute('src'); video.load(); }
    this.#opener?.focus();
  }

  /** Same path as the main button: closes first, so a missing side is flagged in the buy box. */
  add() {
    this.close();
    this.#bundle?.addToCart();
  }
}

if (!customElements.get('stryde-clips-component')) {
  customElements.define('stryde-clips-component', StrydeClipsComponent);
}

/* ------------------------------------------------------------------ *
 * STRYDE sticky bar (homepage) — shows once the first section has
 * scrolled out, button scrolls to an anchor.
 * ------------------------------------------------------------------ */
class StrydeStickyCtaComponent extends Component {
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const first = document.querySelector('main .shopify-section, #MainContent .shopify-section');
    const after = Number(this.dataset.after || 0);
    const onScroll = () => {
      const passed = first ? first.getBoundingClientRect().bottom < -after : false;
      this.classList.toggle('show', passed);
      this.setAttribute('aria-hidden', String(!passed));
    };
    document.addEventListener('scroll', onScroll, { passive: true, capture: true, signal: this.#abort.signal });
    onScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort.abort();
  }

  go() {
    document.getElementById(this.dataset.target || 'product')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

if (!customElements.get('stryde-sticky-cta-component')) {
  customElements.define('stryde-sticky-cta-component', StrydeStickyCtaComponent);
}

/* ------------------------------------------------------------------ *
 * Looping section videos: play only when at least 40% is in view
 * (build order §4). Overrides the native autoplay, which starts at the
 * first visible pixel.
 * ------------------------------------------------------------------ */
function strydeVideoPlayback() {
  for (const video of document.querySelectorAll('.shopify-section video[loop][muted], .shopify-section video[loop][autoplay]')) {
    // Clip thumbnails run their own observer (stryde-clips-component).
    if (!(video instanceof HTMLVideoElement) || video.dataset.strydeIo || video.closest('.scl')) continue;
    video.dataset.strydeIo = '1';
    video.muted = true;
    video.playsInline = true;
    let visible = false;
    video.addEventListener('play', () => { if (!visible) video.pause(); });
    new IntersectionObserver(([entry]) => {
      visible = Boolean(entry && entry.intersectionRatio >= 0.4);
      if (visible) video.play().catch(() => {});
      else video.pause();
    }, { threshold: [0, 0.4] }).observe(video);
  }
}
strydeVideoPlayback();
document.addEventListener('DOMContentLoaded', strydeVideoPlayback);

/* "Choose your size" and similar links: scroll to an anchor, or to the buy block when none is set. */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-stryde-scroll]') : null;
  if (!(link instanceof HTMLElement)) return;
  const id = link.dataset.strydeScroll;
  const target = (id && document.getElementById(id)) || document.querySelector('.product-information');
  if (!target) return;
  event.preventDefault();
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
});
